import { toPath } from 'lodash';

export type SemanticKind = 'product' | 'consumable';

const forbiddenSegments = new Set(['__proto__', 'prototype', 'constructor']);
const scopes = new Set(['self', 'item', 'parent', 'children']);
const hasControlCharacters = (value: string): boolean =>
  Array.from(value).some(character => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });
const pathSyntax =
  /^(?:[^.\[\]\s*?()]+|\[(?:"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')\])(?:\.[^.\[\]\s*?()]+|\[(?:"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')\])*$/;

export function semanticPathSegments(path: string): string[] | undefined {
  if (!pathSyntax.test(path)) return undefined;
  const segments = toPath(path);
  if (
    segments.some(
      segment =>
        !segment ||
        forbiddenSegments.has(segment) ||
        /^\d+$/.test(segment) ||
        hasControlCharacters(segment),
    )
  )
    return undefined;
  return segments;
}

export function canonicalSemanticPath(path: string): string | undefined {
  return semanticPathSegments(path)
    ?.map(segment => `[${JSON.stringify(segment)}]`)
    .join('');
}

export function isSafeFieldLabel(label: string): boolean {
  return (
    Boolean(label.trim()) &&
    label === label.trim() &&
    !forbiddenSegments.has(label) &&
    !hasControlCharacters(label)
  );
}

export function isRelativeSemanticPath(path: string): boolean {
  const segments = semanticPathSegments(path);
  return Boolean(
    segments && !scopes.has(segments[0]) && !segments.includes('components'),
  );
}

export function isFieldPathAllowed(
  path: string,
  kind: SemanticKind,
  collectionMode: boolean,
): boolean {
  const segments = semanticPathSegments(path);
  if (!segments || segments.length < 2 || segments.includes('components'))
    return false;
  const [scope, ...selection] = segments;
  if (selection.some(segment => scopes.has(segment))) return false;
  if (scope === 'self') return true;
  if (scope === 'item') return collectionMode;
  if (scope === 'parent') return kind === 'consumable';
  if (scope === 'children') return kind === 'product';
  return false;
}
